import { useSearchParams } from "react-router";
import {
  deactivateTopicAction,
  deleteTopicAction,
  restoreTopicAction,
} from "@/actions/topics";
import { useApiQuery } from "@/api/useApiQuery";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { PlusIcon } from "@/components/Icon";
import { LinkButton } from "@/components/LinkButton";
import { ListingSummary } from "@/components/ListingSummary";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { StatusBadge } from "@/components/StatusBadge";
import type { TopicListRow } from "@shared/dto";
import { formatUsage } from "@shared/usage";
import { TopicDraftRow } from "./topics/TopicDraftRow";
import { TopicEditRow } from "./topics/TopicEditRow";

const columns: DataTableColumn<TopicListRow>[] = [
  {
    key: "code",
    label: "Code",
    info: "Stable machine identifier, UPPER_SNAKE (e.g. FITNESS). Templates reference it, so it cannot be renamed after use.",
    render: (row) => row.code,
  },
  {
    key: "label",
    label: "Label",
    info: "Human-readable name shown to users, in the default locale (en).",
    render: (row) => row.label ?? "—",
  },
  {
    key: "icon",
    label: "Icon",
    info: "Icon name used for the topic in the app. Free text for now.",
    render: (row) => row.icon ?? "—",
  },
  {
    key: "sortOrder",
    label: "Sort order",
    info: "Position in topic lists; lower numbers appear first.",
    render: (row) => row.sortOrder,
  },
  {
    key: "isActive",
    label: "State",
    render: (row) => (
      <StatusBadge
        label={row.isActive ? "Active" : "Inactive"}
        tone={row.isActive ? "accent" : "neutral"}
      />
    ),
  },
];

export const TopicsPage = () => {
  const [searchParams] = useSearchParams();
  const { data, error, isLoading, refresh } =
    useApiQuery<TopicListRow[]>("/topics");

  const topics = data ?? [];
  const isDraftOpen = searchParams.get("new") === "1";
  const inactiveCount = topics.filter((row) => !row.isActive).length;
  // A half-typed new topic outranks an edit link: the draft would be thrown
  // away by rendering the edit row instead.
  const editingKey = isDraftOpen
    ? undefined
    : (searchParams.get("edit") ?? undefined);

  return (
    <>
      <PageHeader
        kicker="Content"
        title="Topics"
        action={
          isDraftOpen ? null : (
            <LinkButton href="/topics?new=1" variant="primary">
              <PlusIcon size={14} />
              New topic
            </LinkButton>
          )
        }
      />
      <ListingSummary
        counts={[
          { label: "topic", value: topics.length },
          { label: "inactive", value: inactiveCount, shouldPluralize: false },
        ]}
      />
      <DataTable
        label="Topics"
        columns={columns}
        rows={topics}
        getRowKey={(row) => String(row.id)}
        emptyMessage={error ?? (isLoading ? "Loading..." : "No topics yet.")}
        draftRow={isDraftOpen ? <TopicDraftRow onSaved={refresh} /> : undefined}
        editingKey={editingKey}
        renderEditRow={(row) => (
          <TopicEditRow
            id={row.id}
            code={row.code}
            label={row.label}
            icon={row.icon}
            sortOrder={row.sortOrder}
            onSaved={refresh}
          />
        )}
        rowActions={(row) => (
          <RowActions
            editHref={`/topics?edit=${row.id}`}
            isInactive={!row.isActive}
            retireLabel="Deactivate"
            retirePendingLabel="Deactivating..."
            confirmTitle={`Deactivate ${row.code}?`}
            confirmBody={
              <>
                <p>{formatUsage(row.templateCount, "quest template")}</p>
                <p>
                  The topic stays in the database and keeps appearing wherever
                  it is already used, marked as inactive, and its code stays
                  taken. You can restore it from this listing.
                </p>
              </>
            }
            retireAction={deactivateTopicAction.bind(null, row.id)}
            restoreAction={restoreTopicAction.bind(null, row.id)}
            onCompleted={refresh}
            deleteConfirmTitle={`Delete ${row.code}?`}
            deleteConfirmBody={
              <>
                <p>{formatUsage(row.templateCount, "quest template")}</p>
                <p>
                  The topic and its translations are removed for good and the
                  code {row.code} becomes available again. This cannot be
                  undone, and it is refused while any quest template still
                  points at the topic.
                </p>
              </>
            }
            deleteAction={
              row.isActive ? undefined : deleteTopicAction.bind(null, row.id)
            }
          />
        )}
      />
    </>
  );
};
